import React from 'react';
import Icon from '../../../components/AppIcon';

const ReviewStep = ({ formData }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'planning': 'Planning',
      'active': 'Active',
      'on-hold': 'On Hold',
      'completed': 'Completed'
    };
    return statusMap?.[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'planning': 'bg-secondary/10 text-secondary border-secondary/20',
      'active': 'bg-success/10 text-success border-success/20',
      'on-hold': 'bg-warning/10 text-warning border-warning/20',
      'completed': 'bg-muted text-muted-foreground border-border'
    };
    return colorMap?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const sections = [
    {
      title: 'Site Details',
      icon: 'Building2',
      items: [
        { label: 'Site Name', value: formData?.siteName },
        { label: 'Site Reference', value: formData?.siteReference || 'Not provided' },
        { label: 'Start Date', value: formatDate(formData?.startDate) },
        { label: 'End Date', value: formatDate(formData?.endDate) }
      ]
    },
    {
      title: 'Site Address',
      icon: 'MapPin',
      items: [
        { label: 'Address Line 1', value: formData?.addressLine1 },
        { label: 'Address Line 2', value: formData?.addressLine2 || 'Not provided' },
        { label: 'Town/City', value: formData?.city },
        { label: 'County', value: formData?.county || 'Not provided' },
        { label: 'Postcode', value: formData?.postcode }
      ]
    },
    {
      title: 'Client Information',
      icon: 'Users',
      items: [
        { label: 'Client Name', value: formData?.clientName },
        { label: 'Contact Person', value: formData?.clientContactPerson },
        { label: 'Email', value: formData?.clientEmail },
        { label: 'Phone', value: formData?.clientPhone }
      ]
    }
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6">
        <div className="flex gap-3 md:gap-4">
          <div className="flex-shrink-0">
            <Icon name="CheckCircle2" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base lg:text-lg">
              Review Your Site Details
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Please review all information before creating your site. You can edit these details later from the site dashboard.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-foreground text-sm md:text-base lg:text-lg">
            Site Status
          </h4>
          <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium border ${getStatusColor(formData?.status)}`}>
            {getStatusLabel(formData?.status)}
          </span>
        </div>
      </div>
      {sections?.map((section, index) => (
        <div key={index} className="bg-card border border-border rounded-lg p-4 md:p-6">
          <h4 className="font-semibold text-foreground mb-4 text-sm md:text-base lg:text-lg flex items-center gap-2">
            <Icon name={section?.icon} size={20} />
            {section?.title}
          </h4>
          <div className="space-y-3">
            {section?.items?.map((item, itemIndex) => (
              <div key={itemIndex} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                <span className="text-sm md:text-base text-muted-foreground font-medium min-w-[140px]">
                  {item?.label}:
                </span>
                <span className="text-sm md:text-base text-foreground">
                  {item?.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="bg-success/10 border border-success/20 rounded-lg p-4 md:p-6">
        <div className="flex gap-3 md:gap-4">
          <div className="flex-shrink-0">
            <Icon name="Lightbulb" size={24} color="var(--color-success)" />
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-2 text-sm md:text-base lg:text-lg">
              Next Steps After Setup
            </h4>
            <ul className="space-y-2 text-sm md:text-base text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="ArrowRight" size={16} className="flex-shrink-0 mt-1" />
                <span>Upload required documents (H&S, insurance, certificates)</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="ArrowRight" size={16} className="flex-shrink-0 mt-1" />
                <span>Add workers and assign them to this site</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="ArrowRight" size={16} className="flex-shrink-0 mt-1" />
                <span>Complete worker inductions before site access</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="ArrowRight" size={16} className="flex-shrink-0 mt-1" />
                <span>Monitor compliance status from your dashboard</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;